import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Api from '../../Api/api';
import { toast } from 'react-toastify';
import { BeatLoader } from 'react-spinners';
import moment from 'moment';
import { AES, enc } from 'crypto-js';
import { useStateValue } from '../../states/StateProvider';
import ConfirmationModal from '../../components/modal/ConfirmationModal';
import { AiOutlineExclamationCircle } from 'react-icons/ai';

const OpenAppointments = () => {
    const navigate = useNavigate();
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processingAction, setProcessingAction] = useState(false);
    const [userEmail, setUserEmail] = useState('');
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [appointmentToReject, setAppointmentToReject] = useState(null);
    const [{secretKey}, dispatch] = useStateValue();

    useEffect(() => {
        // Get user data from localStorage
        const encryptedUser = localStorage.getItem('user');
        if (!encryptedUser) {
            navigate('/');
            return;
        }

        try {
            const decryptedUser = JSON.parse(AES.decrypt(encryptedUser, secretKey).toString(enc.Utf8));
            setUserEmail(decryptedUser.email);
            fetchOpenAppointments(decryptedUser.email);
        } catch (error) {
            console.error('Error decrypting user data:', error);
            navigate('/');
        }
    }, [navigate, secretKey]);

    const fetchOpenAppointments = async (email) => {
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('email', email);

            const response = await Api().post('/sp_getOpenConsPlan', formData);
            if (response.status === 200) {
                const appointmentData = response.data || [];
                setAppointments(appointmentData);

                // Update global appointment count
                dispatch({
                    type: 'SET_APPOINTMENT_COUNT',
                    item: appointmentData.length
                });
            }
        } catch (error) {
            console.error('Error fetching open appointments:', error);
            toast.error('Fehler beim Laden der offenen Termine');
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmAll = async () => {
        setProcessingAction(true);
        try {
            const formData = new FormData();
            formData.append('email', userEmail);
            formData.append('all', '1');
            formData.append('fromDate', '');
            formData.append('toDate', '');

            const response = await Api().post('/sp_acceptConsPlan', formData);
            if (response.status === 200) {
                toast.success('Alle Termine wurden bestätigt');

                // Update global appointment count to 0 since all are confirmed
                dispatch({
                    type: 'SET_APPOINTMENT_COUNT',
                    item: 0
                });

                navigateToTarget();
            }
        } catch (error) {
            console.error('Error confirming all appointments:', error);
            toast.error('Fehler beim Bestätigen der Termine');
        } finally {
            setProcessingAction(false);
        }
    };

    const navigateToTarget = () => {
        const navigationTarget = localStorage.getItem('pendingNavigation');
        if (navigationTarget === 'firmenprojekte') {
            navigate('/firmenprojekte-liste');
        } else {
            navigate('/dashboard');
        }
        // Clean up
        localStorage.removeItem('pendingNavigation');
    };

    const handleSkip = () => {
        navigateToTarget();
    };

    const handleConfirmAppointment = async (appointment) => {
        setProcessingAction(true);
        try {
            const fromDate = moment(appointment.datum).format('YYYY-MM-DD');
            const toDate = moment(appointment.datum).format('YYYY-MM-DD');

            const formData = new FormData();
            formData.append('email', userEmail);
            formData.append('all', '0');
            formData.append('fromDate', fromDate);
            formData.append('toDate', toDate);

            const response = await Api().post('/sp_acceptConsPlan', formData);
            if (response.status === 200) {
                toast.success('Termin wurde bestätigt');
                await fetchOpenAppointments(userEmail);

                // Update global appointment count
                const newCount = appointments.length - 1;
                dispatch({
                    type: 'SET_APPOINTMENT_COUNT',
                    item: newCount >= 0 ? newCount : 0
                });

                // If no more appointments, navigate to target
                if (appointments.length <= 1) {
                    navigateToTarget();
                }
            }
        } catch (error) {
            console.error('Error confirming appointment:', error);
            toast.error('Fehler beim Bestätigen des Termins');
        } finally {
            setProcessingAction(false);
        }
    };

    const handleRejectAppointment = (appointment) => {
        // Open confirmation modal
        setAppointmentToReject(appointment);
        setShowConfirmModal(true);
    };

    const confirmRejectAppointment = async () => {
        if (!appointmentToReject) return;

        setProcessingAction(true);
        try {
            const formData = new FormData();
            formData.append('email', userEmail);
            formData.append('date', moment(appointmentToReject.datum).format('YYYY-MM-DD'));
            formData.append('fpId', appointmentToReject.FP_ID || appointmentToReject.fpId || '');
            formData.append('plannerEmail', appointmentToReject.planer || '');
            formData.append('reason', ''); // Optional: could add reason input dialog

            const response = await Api().post('/sp_rejectConsAppointment', formData);
            if (response.status === 200) {
                toast.success('Termin wurde erfolgreich abgesagt');
                await fetchOpenAppointments(userEmail);

                // Update global appointment count
                const newCount = appointments.length - 1;
                dispatch({
                    type: 'SET_APPOINTMENT_COUNT',
                    item: newCount >= 0 ? newCount : 0
                });

                // Close modal
                setShowConfirmModal(false);
                setAppointmentToReject(null);

                // If no more appointments, navigate to target
                if (appointments.length <= 1) {
                    navigateToTarget();
                }
            }
        } catch (error) {
            console.error('Error rejecting appointment:', error);
            toast.error('Fehler beim Absagen des Termins');
        } finally {
            setProcessingAction(false);
        }
    };

    const cancelRejectAppointment = () => {
        setShowConfirmModal(false);
        setAppointmentToReject(null);
    };

    return (
        <div className='dashboardContainer'>
            <h2 className="text-2xl text-left text-text font-bold mb-4">Offene Termine</h2>

            <div className="shadow-lg rounded-lg bg-white my-4 border border-borderBlue">
                <div className="p-6" style={{width: '75%', margin: '0 auto'}}>
                    {loading ? (
                        <div className="flex justify-center py-12">
                            <BeatLoader size={12} color={'#3A46A9'}/>
                        </div>
                    ) : appointments.length > 0 ? (
                        <div>
                            {/* Warning text above table */}
                            <div className="mb-4 p-3 bg-yellowLight border-l-4 border-pending">
                                <p className="font-medium text-sm text-orangeDark">
                                    Auflistung der Tage an denen Sie als PFB gebucht werden. Bitte bestätigen Sie die Tage an denen Sie die Buchungsanfrage annehmen.
                                </p>
                            </div>

                            {/* Buttons above table */}
                            <div className="mb-4 flex flex-wrap gap-3 justify-end">
                                <button
                                    type="button"
                                    onClick={handleSkip}
                                    disabled={processingAction}
                                    className="px-5 py-2.5 text-sm text-white bg-mainBlue rounded-lg shadow-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mainBlue transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Später erinnern
                                </button>

                                <button
                                    type="button"
                                    onClick={handleConfirmAll}
                                    disabled={processingAction}
                                    className="px-5 py-2.5 text-sm text-white bg-tahiti border border-transparent rounded-lg shadow-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {processingAction ? (
                                        <BeatLoader size={8} color={'#ffffff'}/>
                                    ) : (
                                        'Alle Termine bestätigen'
                                    )}
                                </button>
                            </div>

                            <div className="overflow-x-auto shadow-md border border-borderBlue rounded-xl">
                                <table className="min-w-full divide-y divide-borderBlue">
                                    <thead className="bg-offWhite">
                                    <tr>
                                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-text uppercase tracking-wider" style={{width: '30%'}}>
                                            Firma
                                        </th>
                                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-text uppercase tracking-wider">
                                            Datum
                                        </th>
                                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-text uppercase tracking-wider">
                                            Grund
                                        </th>
                                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-text uppercase tracking-wider" style={{width: '20%'}}>
                                            Planer
                                        </th>
                                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-text uppercase tracking-wider">
                                            <div className="flex gap-2 justify-end">Aktionen</div>
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-borderBlue">
                                    {appointments.map((appointment, index) => {
                                        return (
                                            <tr
                                                key={index}
                                                className="hover:bg-lightgrey transition-all duration-200"
                                            >
                                                <td className="px-4 py-2.5 whitespace-nowrap text-sm font-semibold text-text text-left" style={{width: '30%'}}>
                                                    {appointment.Firma}
                                                </td>
                                                <td className="px-4 py-2.5 whitespace-nowrap text-sm text-text text-left">
                                                    {moment(appointment.datum).format('DD.MM.YYYY')}
                                                </td>
                                                <td className="px-4 py-2.5 whitespace-nowrap text-sm text-cancel font-medium text-left">
                                                    Buchungsanfrage PFB
                                                </td>
                                                <td className="px-4 py-2.5 whitespace-nowrap text-sm text-text text-left" style={{width: '20%'}}>
                                                    {appointment.planer}
                                                </td>
                                                <td className="px-4 py-2.5 whitespace-nowrap">
                                                    <div className="flex gap-2 justify-end">
                                                        <button
                                                            onClick={() => handleConfirmAppointment(appointment)}
                                                            disabled={processingAction}
                                                            className="px-3 py-1.5 text-xs text-white bg-mainBlue rounded-md hover:bg-opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                                        >
                                                            Bestätigen
                                                        </button>
                                                        <button
                                                            onClick={() => handleRejectAppointment(appointment)}
                                                            disabled={processingAction}
                                                            className="px-3 py-1.5 text-xs text-white bg-cancel rounded-md hover:bg-opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                                        >
                                                            Absagen
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Buttons below table */}
                            <div className="mt-6 flex flex-wrap gap-3 justify-end">
                                <button
                                    type="button"
                                    onClick={handleSkip}
                                    disabled={processingAction}
                                    className="px-5 py-2.5 text-sm text-white bg-mainBlue rounded-lg shadow-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mainBlue transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Später erinnern
                                </button>

                                <button
                                    type="button"
                                    onClick={handleConfirmAll}
                                    disabled={processingAction}
                                    className="px-5 py-2.5 text-sm text-white bg-tahiti border border-transparent rounded-lg shadow-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {processingAction ? (
                                        <BeatLoader size={8} color={'#ffffff'}/>
                                    ) : (
                                        'Alle Termine bestätigen'
                                    )}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-16">
                            <div
                                className="inline-flex items-center justify-center w-16 h-16 bg-tahiti bg-opacity-10 rounded-full mb-4">
                                <svg className="h-8 w-8 text-green" fill="none" stroke="currentColor"
                                     viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                </svg>
                            </div>
                            <h3 className="text-lg font-semibold text-text mb-2">Keine offenen Termine</h3>
                            <p className="text-base text-grey mb-6">
                                Sie haben keine unbestätigten Termine.
                            </p>
                            <div>
                                <button
                                    type="button"
                                    onClick={navigateToTarget}
                                    className="inline-flex items-center px-6 py-3 border border-transparent shadow-md text-sm font-semibold rounded-lg text-white bg-mainBlue hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mainBlue transition-all"
                                >
                                    Weiter zum Dashboard
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Confirmation Modal for Rejecting Appointment */}
            <ConfirmationModal
                visible={showConfirmModal}
                title="Termin absagen"
                message={appointmentToReject ?
                    `Möchten Sie den Termin am ${moment(appointmentToReject.datum).format('DD.MM.YYYY')} bei ${appointmentToReject.Firma} wirklich absagen?` :
                    'Möchten Sie diesen Termin wirklich absagen?'
                }
                confirmText="Ja, absagen"
                cancelText="Abbrechen"
                onConfirm={confirmRejectAppointment}
                onCancel={cancelRejectAppointment}
                loading={processingAction}
                fullWidthButtons={true}
                icon={<AiOutlineExclamationCircle size={28} className="text-cancel" />}
            />
        </div>
    );
};

export default OpenAppointments;
